// Error handling utilities

export interface ErrorInfo {
  message: string;
  code?: string;
  details?: any;
  timestamp: number;
}

export class AppError extends Error {
  public code?: string;
  public details?: any;
  public timestamp: number;

  constructor(message: string, code?: string, details?: any) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.details = details;
    this.timestamp = Date.now();
  }
}

/**
 * Session storage error handling utilities
 */
export class SessionStorageManager {
  private static readonly FALLBACK_STORAGE = new Map<string, string>();

  static setItem(key: string, value: string): boolean {
    try {
      sessionStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.warn('Session storage unavailable, using fallback:', error);
      this.FALLBACK_STORAGE.set(key, value);
      return false;
    }
  }

  static getItem(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      console.warn('Session storage unavailable, using fallback:', error);
      return this.FALLBACK_STORAGE.get(key) || null;
    }
  }

  static removeItem(key: string): boolean {
    try {
      sessionStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn('Session storage unavailable, using fallback:', error);
      this.FALLBACK_STORAGE.delete(key);
      return false;
    }
  }

  static clear(): boolean {
    try {
      sessionStorage.clear();
      return true;
    } catch (error) {
      console.warn('Session storage unavailable, using fallback:', error);
      this.FALLBACK_STORAGE.clear();
      return false;
    }
  }

  static isAvailable(): boolean {
    try {
      const testKey = '__test_storage__';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Image loading error handling
 */
export const getImageFallbackUrl = (originalUrl: string, seed?: string): string => {
  // Generate a consistent fallback based on the original URL or seed
  if (seed) {
    return `https://picsum.photos/seed/${seed}/600/400`;
  }
  
  // Create a more unique seed by hashing the URL
  let hash = 0;
  for (let i = 0; i < originalUrl.length; i++) {
    const char = originalUrl.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  const uniqueSeed = Math.abs(hash).toString(36);
  return `https://picsum.photos/seed/${uniqueSeed}/600/400`;
};

/**
 * Form validation error handling
 */
export const formatValidationErrors = (errors: any[]): Record<string, string> => {
  const errorMap: Record<string, string> = {};
  
  errors.forEach(error => {
    if (error.field && error.message) {
      errorMap[error.field] = error.message;
    }
  });
  
  return errorMap;
};

/**
 * Retry utility with exponential backoff
 */
export const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: any;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      if (attempt < maxRetries - 1) {
        const delay = baseDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError;
};

/**
 * Generate mailto fallback URL for failed form submissions
 */
export const generateMailtoFallback = (
  to: string,
  subject: string,
  body: string
): string => {
  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};